"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import Image from "next/image"
import {
  Settings,
  Phone,
  Users,
  AlertCircle,
  ChevronRight,
  UserCog,
  ShieldOff,
  UserCircle,
  Mail,
  ShoppingBag,
  Package,
  LayoutDashboard,
  BarChart3,
  Building2,
  Plug,
  Bot,
  BookOpen,
  ScrollText,
  MessageCircle,
  CreditCard,
  Receipt,
  Calculator,
  DollarSign,
  Boxes,
  Bell,
  KeyRound,
  Handshake,
  BellRing,
  Loader2,
  Megaphone,
  ListTodo,
  Inbox,
} from "lucide-react"
import { LoginForm } from "@/components/auth/login-form"
import { useAuth, type AuthUser } from "@/hooks/use-auth"
import { VerifyEmailBanner } from "@/components/auth/verify-email-banner"
import { usePermissions } from "@/hooks/use-permissions"
import { EnvironmentBadge } from "@/components/environment-badge"
import { VoiceAgentsPage } from "@/components/agents/voice"
import { UserManagementWithInvite } from "@/components/admin/user-management-with-invite"
import { CallsPage } from "@/components/calls/calls-page"
import { useSystemAccess } from "@/hooks/use-system-access"
import { SystemSettingsPage } from "@/components/system-settings/system-settings-page"
import { UserProfilePage } from "@/components/user-profile/user-profile-page"
import { EmailAgentsPage, EmailAgentInboxPage } from "@/components/agents/email"
import { AgentAnalyticsDashboard } from "@/components/agents/email/analytics"
import { ConversationQualityPage } from "@/components/conversation-quality/conversation-quality-page"
import { ShopifyOrdersPage } from "@/components/shopify/orders-page"
import { ShopifyDashboardPage } from "@/components/shopify/dashboard-page"
import { ShopifyInsightsGate } from "@/components/shopify/shopify-insights-gate"
import { useShopify } from "@/hooks/use-shopify"
import { TenantSwitcher } from "@/components/tenant-switcher"
import { TenantManagementPage } from "@/components/tenant-management/tenant-management-page"
import { ArchivedWarningScreen } from "@/components/organization/archived-warning-screen"
import { TenantInfo } from "@/components/tenant-info"
import { useCurrentTenantQuery } from "@/hooks/queries/current-tenant"
import { IntegrationsPage } from "@/components/integrations/integrations-page"
import { ShopifyAttachHandler } from "@/components/integrations/shopify-attach-handler"
import { SHOPIFY_ATTACH_PARAM } from "@/lib/shopify/auth/attach-link"
import { NotificationChannelsPage } from "@/components/notification-channels/notification-channels-page"
import { SystemNotificationsPage } from "@/components/system-notifications"
import { ApiKeysPage } from "@/components/api-keys/api-keys-page"
import { PartnerProgramPage } from "@/components/partners/partner-program-page"
import { PartnerAdminPage } from "@/components/partners/admin/partner-admin-page"
import { AISettingsPage } from "@/components/ai/ai-settings-page"
import { AILogsView } from "@/components/ai/logs/ai-logs-view"
import { QAFeedbackPage } from "@/components/ai/feedback/qa-feedback-page"
import { KnowledgeBasePage } from "@/components/knowledge-base"
import { SHOPIFY_PERMISSIONS, VOICE_AGENTS_PERMISSIONS, EMAIL_AGENTS_PERMISSIONS, EMAIL_PERMISSIONS, CALLS_PERMISSIONS, USERS_PERMISSIONS, AI_PERMISSIONS, INTEGRATIONS_PERMISSIONS, PBX_PERMISSIONS, MESSAGING_AGENTS_PERMISSIONS, BILLING_PERMISSIONS, NOTIFICATIONS_PERMISSIONS, TENANTS_PERMISSIONS, API_KEYS_PERMISSIONS, AGENT_TASKS_PERMISSIONS, SYSTEM_NOTIFICATIONS_PERMISSIONS, PARTNER_PROGRAM_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { PbxPage } from "@/components/pbx/pages"
import { MessagingAgentsPage } from "@/components/agents/messaging"
import { ConversationsInboxPage } from "@/components/agents/messaging/inbox/conversations-inbox-page"
import { UnifiedInboxPage } from "@/components/inbox/unified-inbox-page"
import { buildInboxAttentionBadge } from "@/components/inbox/attention-badge"
import {
  inboxVisitKey,
  inboxVisitStorage,
  readInboxVisit,
  rememberInboxVisit,
} from "@/components/inbox/inbox-visit"
import { useInboxAttentionSummary } from "@/hooks/queries/unified-inbox"
import { usePlatformLogo } from "@/hooks/use-platform-logo"
import { CrossSellPage } from "@/components/shopify/cross-sell"
import { EconomicsPage } from "@/components/shopify/economics"
import { CatalogPage } from "@/components/shopify/catalog"
import { BundleRulesPage } from "@/components/shopify/bundle-rules"
import { TenantBillingPage, BillingPage, LowBalanceBanner } from "@/components/billing"
import { InitialSyncBanner } from "@/components/shopify/initial-sync-banner"
import { PlanExpiredDialog } from "@/components/billing/plan-expired-dialog"
import { CampaignsPage } from "@/components/campaigns"
import {
  usePlanFeatureAccessQuery,
  useRefetchPlanFeatureAccessOnPlanChange,
} from "@/hooks/queries/plan-feature-access"
import {
  featureLockHint,
  type FeatureLockReason,
  type PlanFeature,
  type PlanFeatureAccess,
} from "@/lib/billing/feature-access/rules"
import { useBillingOverviewQuery } from "@/hooks/queries/billing-overview"
import { usePartnerProgramAccessQuery } from "@/hooks/queries/partners"
import type { BillingOverviewResponse } from "@/lib/billing/types"
import { AppStartupLoader } from "@/components/app-startup-loader"
import { AppSidebar, type SidebarItem, type SidebarSection } from "@/components/app-sidebar"
import { OnboardingPage } from "@/components/onboarding/onboarding-page"
import {
  LEGACY_SIGNUP_ONBOARDING_PAGE,
  SIGNUP_ONBOARDING_PATH,
} from "@/lib/onboarding/constants"
import { useSignupOnboardingQuery } from "@/hooks/queries/signup-onboarding"
import {
  SignupOnboardingCompletionHint,
  SignupOnboardingReminder,
} from "@/components/onboarding/signup-onboarding-reminder"
import { SupportChatContext } from "@/components/support/support-chat-context"
import { AgentTasksPage } from "@/components/agent-tasks"
import { useLocale, useTranslations } from "next-intl"



const VALID_PAGES = [
  "voice-agents",
  "user-management",
  "call-logs",
  "system-settings",
  "user-profile",
  "integrations",
  "notification-channels",
  "api-keys",
  "partner-program",
  "partner-admin",
  "email-agent",
  "email-agents",
  "agent-emails",
  "agent-analytics",
  "agent-feedback",
  "shopify-dashboard",
  "shopify-orders",
  "shopify-products",
  "shopify-economics",
  "shopify-catalog",
  "shopify-bundle-rules",
  "tenant-management",
  "ai-settings",
  "ai-logs",
  "qa-feedback",
  "knowledge-base",
  "pbx",
  "messaging-agents",
  "message-conversations",
  "billing",
  "billing-admin",
  "system-notifications",
  "campaigns",
  "agent-tasks",
  "unified-inbox",
  "conversation-quality",
] as const

type PageKey = (typeof VALID_PAGES)[number]

const VALID_PAGE_SET: ReadonlySet<string> = new Set(VALID_PAGES)

/** Pages the plan can lock; the nav item, the page guard and the redirect all follow it. */
const PAGE_PLAN_FEATURES: Partial<Record<PageKey, PlanFeature>> = {
  "voice-agents": "voice_agents",
  campaigns: "campaigns",
}

// Query params that survive a top-level page switch. Anything not in this
// set is treated as the previous page's state and dropped on navigation —
// every page re-hydrates its own URL params from its own defaults on mount.
// To make a param cross-page, add it here. Don't sprinkle delete() calls
// at each call site — that's how matrix_*, bundles, show_hypothetical etc.
// leaked into /?page=billing.
const CROSS_PAGE_QUERY_PARAMS = new Set<string>(["page"])
const SIDEBAR_OPEN_SECTIONS_STORAGE_KEY = "versafy.sidebar.openSections"

function stripPageScopedQueryParams(url: URL): void {
  const drop: string[] = []
  url.searchParams.forEach((_, key) => {
    if (!CROSS_PAGE_QUERY_PARAMS.has(key)) drop.push(key)
  })
  for (const key of drop) url.searchParams.delete(key)
}

function Dashboard({
  user,
  onLogout,
  isTenantInactive,
}: {
  user: AuthUser
  onLogout: () => void
  isTenantInactive: boolean
}) {
  const tAttention = useTranslations("InboxAttention")
  const uiLocale = useLocale()
  const tUi = useTranslations("Shell.Page")

  const searchParams = useSearchParams()
  const { hasIntegration: hasShopifyIntegration } = useShopify()
  const shopifyEnabled = hasShopifyIntegration()
  const { can } = usePermissions(user)
  const { logoUrl: platformLogoUrl, platformName, isCustom: isCustomLogo } = usePlatformLogo()
  const { data: planFeatureAccess } = usePlanFeatureAccessQuery()
  useRefetchPlanFeatureAccessOnPlanChange()
  const partnerProgramAccess = usePartnerProgramAccessQuery()

  // Billing mode — determines nav visibility for manual tenants.
  // Tri-state: 'loading' (hide nav until known), 'manual', or provider name.
  const hasBillingView = can(BILLING_PERMISSIONS.VIEW)
  const billingOverview = useBillingOverviewQuery()
  const billingMode = resolveBillingMode(hasBillingView, billingOverview.data)

  const isValidPage = (page: string | null): page is PageKey => !!page && VALID_PAGE_SET.has(page)

  const normalizePageParam = (page: string | null): PageKey => {
    if (page === "ai-providers" || page === "ai-config") return "ai-settings"
    // Backwards compatibility for old page names
    if (page === "saved-agents" || page === "voice-agent") return "voice-agents"
    // shopify-cogs → shopify-catalog (the catalog page wraps the legacy COGS
    // table under its "cogs" tab; keep this alias for one release so old
    // bookmarks / shared links don't 404).
    if (page === "shopify-cogs") return "shopify-catalog"
    // organization → user-profile (Phase-5 consolidated Org under
    // user-profile tabs). The `?tab=organization` pin is done in
    // `getInitialPage` for first navigation; for subsequent transitions
    // the tab param is irrelevant — the user-profile component reads it
    // on mount only.
    if (page === "organization") return "user-profile"
    if (isValidPage(page)) return page
    return "billing"
  }

  const getInitialPage = (): PageKey => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      const page = urlParams.get("page")
      if (page === "shopify") {
        return "shopify-dashboard"
      }
      // Legacy `?page=shopify-cogs` bookmarks: redirect to the new catalog page
      // AND preserve the user's intent by pinning the COGS tab. Without `tab=cogs`
      // `CatalogPage` would default to the overview placeholder, which silently
      // breaks the one-release back-compat promise. `replaceState` rewrites the
      // URL in place — the back button still walks pages, not tabs.
      if (page === "shopify-cogs") {
        const url = new URL(window.location.href)
        url.searchParams.set("page", "shopify-catalog")
        if (!url.searchParams.has("tab")) url.searchParams.set("tab", "cogs")
        window.history.replaceState({}, "", url.toString())
        return "shopify-catalog"
      }
      // Legacy `?page=organization` bookmarks: redirect to user-profile and
      // pin the organization tab (Phase-5 consolidated Org under Profile).
      if (page === "organization") {
        const url = new URL(window.location.href)
        url.searchParams.set("page", "user-profile")
        if (!url.searchParams.has("tab")) url.searchParams.set("tab", "organization")
        window.history.replaceState({}, "", url.toString())
        return "user-profile"
      }
      return normalizePageParam(page)
    }
    return "billing"
  }

  const initialPage = getInitialPage()
  const [currentPage, setCurrentPage] = useState<PageKey>(initialPage)

  const isSuperUser = user && user.role === "super_user"
  // Whether the user's HOME tenant is the platform tenant (JWT claim). Platform-
  // scoped surfaces (Tenant Management, Billing Admin) require this for the
  // delegated `can()` branch — a cross-tenant permission granted to a NON-platform
  // user must not unlock them. The server re-checks via requirePlatformPermission.
  const isPlatformTenant = !!user?.is_platform_tenant
  // Always the platform's own settings, so it stays reachable while impersonating.
  const canManageSystemNotifications =
    !!isSuperUser || (isPlatformTenant && can(SYSTEM_NOTIFICATIONS_PERMISSIONS.MANAGE))
  // Integrators switch tenants and use Tenant Management for platform support,
  // but remain excluded from super-user-only System Settings, Billing Admin,
  // tenant lifecycle, and other provider-administration surfaces.
  const canSwitchTenant = !!user && (user.isSuperUser || user.isIntegrator)

  // Permission-based access checks for navigation guards
  const canAccessUserManagement = can(USERS_PERMISSIONS.VIEW)
  const canAccessUnifiedInbox =
    can(MESSAGING_AGENTS_PERMISSIONS.VIEW) || can(EMAIL_PERMISSIONS.VIEW) || can(CALLS_PERMISSIONS.VIEW)
  const canAccessSystemSettings = isSuperUser
  const canAccessAISettings = isSuperUser // AI Settings only for super_user via System Settings

  // Single source for page access — initial routing, searchParam sync, nav, and
  // render all consult it so the gated pages can't drift. Unlisted pages are
  // open (their own component/API guards). Platform-scoped pages require super_user
  // OR a platform-tenant user holding the delegated permission — never a
  // non-platform user, even if granted the cross-tenant permission.
  const isPageLocked = useCallback(
    (page: PageKey) => (isTenantInactive && page !== "billing") || lockedPlanFeature(planFeatureAccess, page) !== null,
    [isTenantInactive, planFeatureAccess],
  )

  const canAccessPage = (page: PageKey): boolean => {
    if (isPageLocked(page)) return false
    switch (page) {
      case "user-management":
        return canAccessUserManagement
      case "system-settings":
        return canAccessSystemSettings
      case "ai-settings":
        return canAccessAISettings
      case "tenant-management":
        return canSwitchTenant || (isPlatformTenant && can(TENANTS_PERMISSIONS.VIEW))
      case "billing-admin":
        return !!isSuperUser || (isPlatformTenant && (can(BILLING_PERMISSIONS.VIEW_ALL) || can(BILLING_PERMISSIONS.VIEW_TENANTS) || can(BILLING_PERMISSIONS.MANAGE_TENANTS)))
      case "system-notifications":
        return canManageSystemNotifications
      case "api-keys":
        return can(API_KEYS_PERMISSIONS.MANAGE)
      // Refused only once the program is known off, so a deep link is not bounced while it loads.
      case "partner-program":
        return can(PARTNER_PROGRAM_PERMISSIONS.MANAGE) && partnerProgramAccess.data !== false
      case "partner-admin":
        return !!isSuperUser || (isPlatformTenant && can(PARTNER_PROGRAM_PERMISSIONS.ADMIN))
      case "campaigns":
        return can(VOICE_AGENTS_PERMISSIONS.MANAGE)
      case "agent-tasks":
        return can(AGENT_TASKS_PERMISSIONS.VIEW)
      case "conversation-quality":
        return can(MESSAGING_AGENTS_PERMISSIONS.ANALYTICS)
      // No permission of its own: readable when the operator may read at least
      // one of the conversation sources it merges.
      case "unified-inbox":
        return canAccessUnifiedInbox
      default:
        return true
    }
  }

  const sidebarSections = useMemo<SidebarSection<PageKey>[]>(() => withPlanLocks(planFeatureAccess, hasBillingView, [
    {
      id: "general",
      title: tUi("general"),
      icon: CreditCard,
      items: [
        {
          id: "billing",
          label: tUi("billing"),
          icon: CreditCard,
          page: "billing",
          isVisible: () =>
            isTenantInactive ||
            (can(BILLING_PERMISSIONS.VIEW) && billingMode !== "loading"),
        },
        {
          id: "user-profile",
          label: tUi("accountSettings"),
          icon: UserCircle,
          page: "user-profile",
        },
        {
          id: "user-management",
          label: tUi("userManagement"),
          icon: UserCog,
          page: "user-management",
          isVisible: () => can(USERS_PERMISSIONS.VIEW),
        },
        {
          id: "integrations",
          label: tUi("integrations"),
          icon: Plug,
          page: "integrations",
          isVisible: () => can(INTEGRATIONS_PERMISSIONS.MANAGE),
        },
        {
          id: "knowledge-base",
          label: tUi("knowledgeBase"),
          icon: BookOpen,
          page: "knowledge-base",
          isVisible: () => can(AI_PERMISSIONS.VIEW),
        },
        {
          id: "notification-channels",
          label: tUi("notificationChannels"),
          icon: Bell,
          page: "notification-channels",
          isVisible: () => can(NOTIFICATIONS_PERMISSIONS.MANAGE),
        },
        {
          id: "agent-tasks",
          label: tUi("agentTasks"),
          icon: ListTodo,
          page: "agent-tasks",
          isVisible: () => can(AGENT_TASKS_PERMISSIONS.VIEW),
        },
        {
          id: "docs",
          label: tUi("docs"),
          icon: BookOpen,
          href: "https://docs.versafy.ai",
        },
      ],
    },
    {
      id: "phone",
      title: tUi("phone"),
      icon: Phone,
      items: [
        {
          id: "pbx",
          label: tUi("phoneNumbers"),
          icon: Phone,
          page: "pbx",
          isVisible: () => can(PBX_PERMISSIONS.VIEW),
        },
        {
          id: "voice-agents",
          label: tUi("voiceAgents"),
          icon: Bot,
          page: "voice-agents",
          isVisible: () => can(VOICE_AGENTS_PERMISSIONS.VIEW),
        },
        {
          id: "campaigns",
          label: tUi("campaigns"),
          icon: Megaphone,
          page: "campaigns",
          isVisible: () => can(VOICE_AGENTS_PERMISSIONS.MANAGE),
        },
        {
          id: "call-logs",
          label: tUi("callLogsRecordings"),
          icon: Phone,
          page: "call-logs",
        },
      ],
    },
    {
      id: "email-messaging",
      title: tUi("emailMessaging"),
      icon: Mail,
      items: [
        {
          id: "email-agents",
          label: tUi("emailAgents"),
          icon: Mail,
          page: "email-agents",
          isVisible: () => can(EMAIL_AGENTS_PERMISSIONS.VIEW),
        },
        {
          id: "messaging-agents",
          label: tUi("messagingAgents"),
          icon: MessageCircle,
          page: "messaging-agents",
          isVisible: () => can(MESSAGING_AGENTS_PERMISSIONS.VIEW),
        },
        {
          id: "conversation-quality",
          label: tUi("conversationQuality"),
          icon: BarChart3,
          page: "conversation-quality",
          isVisible: () => can(MESSAGING_AGENTS_PERMISSIONS.ANALYTICS),
        },
      ],
    },
    {
      id: "shopify",
      title: "Shopify",
      icon: ShoppingBag,
      items: [
        {
          id: "shopify-dashboard",
          label: tUi("dashboard"),
          icon: LayoutDashboard,
          page: "shopify-dashboard",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
        {
          id: "shopify-orders",
          label: tUi("orders"),
          icon: Package,
          page: "shopify-orders",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
        {
          id: "shopify-products",
          label: tUi("crossSell"),
          icon: BarChart3,
          page: "shopify-products",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
        {
          id: "shopify-economics",
          label: tUi("productEconomics"),
          icon: Calculator,
          page: "shopify-economics",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
        {
          id: "shopify-catalog",
          label: tUi("shopifyCatalog"),
          icon: DollarSign,
          page: "shopify-catalog",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
        {
          id: "shopify-bundle-rules",
          label: tUi("bundlesFamilies"),
          icon: Boxes,
          page: "shopify-bundle-rules",
          isVisible: () => shopifyEnabled && can(SHOPIFY_PERMISSIONS.VIEW),
        },
      ],
    },
    {
      id: "super-admin",
      title: isSuperUser ? "Super Admin" : "Platform",
      icon: ShieldOff,
      items: [
        {
          id: "system-settings",
          label: tUi("systemSettings"),
          icon: Settings,
          page: "system-settings",
          isVisible: () => !!isSuperUser,
        },
        {
          id: "billing-admin",
          label: tUi("billingAdmin"),
          icon: Receipt,
          page: "billing-admin",
          isVisible: () => !!isSuperUser || (isPlatformTenant && (can(BILLING_PERMISSIONS.VIEW_ALL) || can(BILLING_PERMISSIONS.VIEW_TENANTS) || can(BILLING_PERMISSIONS.MANAGE_TENANTS))),
        },
        {
          id: "tenant-management",
          label: tUi("tenantManagement"),
          icon: Building2,
          page: "tenant-management",
          isVisible: () => canSwitchTenant || (isPlatformTenant && can(TENANTS_PERMISSIONS.VIEW)),
        },
        {
          id: "partner-admin",
          label: tUi("partnerProgram"),
          icon: Handshake,
          page: "partner-admin",
          isVisible: () => !!isSuperUser || (isPlatformTenant && can(PARTNER_PROGRAM_PERMISSIONS.ADMIN)),
        },
        {
          id: "system-notifications",
          label: tUi("systemNotifications"),
          icon: BellRing,
          page: "system-notifications",
          isVisible: () => canManageSystemNotifications,
        },
      ],
    },
    {
      id: "partner-program",
      items: [
        {
          id: "partner-program",
          label: tUi("partnerProgram"),
          icon: Handshake,
          highlighted: true,
          page: "partner-program",
          isVisible: () => can(PARTNER_PROGRAM_PERMISSIONS.MANAGE) && partnerProgramAccess.data === true,
        },
      ],
    },
    {
      id: "misc",
      title: tUi("misc"),
      icon: ScrollText,
      items: [
        {
          id: "api-keys",
          label: tUi("aPIKeys"),
          icon: KeyRound,
          page: "api-keys",
          isVisible: () => can(API_KEYS_PERMISSIONS.MANAGE),
        },
        {
          id: "ai-logs",
          label: tUi("aILogs"),
          icon: ScrollText,
          page: "ai-logs",
          isVisible: () => can(AI_PERMISSIONS.VIEW_LOGS),
        },
        {
          id: "contacts",
          label: tUi("contacts"),
          icon: Users,
          isVisible: () => !!isSuperUser,
        },
        {
          id: "error-logs",
          label: tUi("errorLogs"),
          icon: AlertCircle,
          isVisible: () => !!isSuperUser,
        },
      ],
    },
  ], tUi), [shopifyEnabled, isSuperUser, isPlatformTenant, canManageSystemNotifications, can, billingMode, planFeatureAccess, hasBillingView, isTenantInactive, canSwitchTenant, partnerProgramAccess.data, tUi])

  // The customer's ask: one always-visible entry point carrying how many
  // conversations are waiting for a person. `isVisible` governs the nav — the
  // page guard in `canAccessPage` is never consulted for rendering.
  // An inactive tenant sees every item locked, so the badge would be a poll a
  // minute for a number nothing renders.
  const visitKey = inboxVisitKey(user.user_id, user.impersonated_tenant_id ?? user.tenant_id)
  const [visitedAt, setVisitedAt] = useState(() => readInboxVisit(inboxVisitStorage(), visitKey))
  const attentionSummary = useInboxAttentionSummary({
    enabled: canAccessUnifiedInbox && !isTenantInactive,
    since: visitedAt,
  })
  useStampInboxVisit(
    currentPage === "unified-inbox",
    attentionSummary.data?.countedAt,
    visitKey,
    setVisitedAt,
  )
  const pinnedItems = useMemo<SidebarItem<PageKey>[]>(() => [
    {
      id: "unified-inbox",
      label: tUi("chats"),
      icon: Inbox,
      page: "unified-inbox",
      prominent: true,
      isVisible: () => canAccessUnifiedInbox,
      badge: buildInboxAttentionBadge(attentionSummary.data, tAttention, uiLocale),
    },
  ], [canAccessUnifiedInbox, attentionSummary.data, tUi, tAttention, uiLocale])

  useEffect(() => {
    if (!isPageLocked(currentPage)) return
    const url = new URL(window.location.href)
    stripPageScopedQueryParams(url)
    url.searchParams.set("page", "billing")
    window.history.replaceState({}, "", url.toString())
    setCurrentPage("billing")
  }, [currentPage, isPageLocked])

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    sidebarSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children?.some((child) => child.page === initialPage)) {
          initial[item.id] = true
        }
      })
    })
    return initial
  })
  const [openSectionIds, setOpenSectionIds] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return { general: true }

    try {
      const storedValue = window.localStorage.getItem(SIDEBAR_OPEN_SECTIONS_STORAGE_KEY)
      if (!storedValue) return { general: true }
      const parsedValue: unknown = JSON.parse(storedValue)
      if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
        return { general: true }
      }

      return Object.entries(parsedValue).reduce<Record<string, boolean>>((result, [key, value]) => {
        if (typeof value === "boolean") {
          result[key] = value
        }
        return result
      }, {})
    } catch {
      return { general: true }
    }
  })

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_OPEN_SECTIONS_STORAGE_KEY, JSON.stringify(openSectionIds))
  }, [openSectionIds])

  // Defensive sync: if currentPage was flipped without the URL being updated
  // first (handlePageChange and the router.push sync effect both update URL
  // before setCurrentPage, so this is a safety net), strip stale params and
  // pin ?page=. Cross-page params would be preserved via the allowlist.
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("page") !== currentPage) {
      stripPageScopedQueryParams(url)
      url.searchParams.set("page", currentPage)
      window.history.replaceState({}, "", url.toString())
    }
  }, [currentPage])

  useEffect(() => {
    const activeSection = sidebarSections.find((section) =>
      section.items.some((item) => item.page === currentPage || item.children?.some((child) => child.page === currentPage)),
    )
    if (activeSection) {
      setOpenSectionIds((prev) => (prev[activeSection.id] ? prev : { ...prev, [activeSection.id]: true }))
    }

    sidebarSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children?.some((child) => child.page === currentPage)) {
          setExpandedItems((prev) => {
            if (prev[item.id]) {
              return prev
            }
            return { ...prev, [item.id]: true }
          })
        }
      })
    })
  }, [currentPage, sidebarSections])

  // Read URL parameters on mount and handle initial routing
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get("page")

    if (pageParam === "shopify") {
      setCurrentPage("shopify-dashboard")
      const url = new URL(window.location.href)
      url.searchParams.set("page", "shopify-dashboard")
      window.history.replaceState({}, "", url.toString())
    } else if (isValidPage(pageParam) || pageParam === "ai-providers" || pageParam === "ai-config" || pageParam === "saved-agents" || pageParam === "voice-agent") {
      const normalized = normalizePageParam(pageParam)
      if (!canAccessPage(normalized)) {
        setCurrentPage("billing")
        const url = new URL(window.location.href)
        url.searchParams.set("page", "billing")
        window.history.replaceState({}, "", url.toString())
      } else {
        setCurrentPage(normalized)
      }
    } else {
      setCurrentPage("billing")
      const url = new URL(window.location.href)
      url.searchParams.set("page", "billing")
      window.history.replaceState({}, "", url.toString())
    }
    // `isValidPage` / `normalizePageParam` are stable closures over the
    // module-level page set — intentionally omitted from deps so this
    // effect only re-runs on auth/permission changes, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessUserManagement, canAccessSystemSettings, canAccessAISettings, isSuperUser, isPlatformTenant])

  // Sync currentPage with URL when searchParams change (for router.push navigation)
  useEffect(() => {
    const pageParam = searchParams.get("page")
    if (pageParam) {
      const normalized = normalizePageParam(pageParam)
      // Check access before changing page (single source: canAccessPage)
      if (!canAccessPage(normalized)) return
      setCurrentPage(normalized)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const toggleSection = (sectionId: string) => {
    setOpenSectionIds((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const toggleSidebarItem = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  const handlePageChange = (page: PageKey) => {
    if (!canAccessPage(page)) {
      return
    }

    // Update URL BEFORE state change to ensure child components get clean searchParams
    // (prevents flash of stale agentId from previous page e.g. email-agents → call-logs).
    const url = new URL(window.location.href)
    stripPageScopedQueryParams(url)
    url.searchParams.set("page", page)
    window.history.replaceState({}, "", url.toString())

    setCurrentPage(page)
  }

  // Sub-params (agentId, tab, …) are intentionally dropped so a new tab lands
  // on the page's default view — same contract as handlePageChange.
  const buildPageHref = (page: PageKey) => `?page=${page}`

  // Never mount an inaccessible page, even for the frame before the routing
  // effects redirect, so restricted page data is not fetched accidentally.
  const activePage: PageKey = canAccessPage(currentPage) ? currentPage : "billing"

  const getPageTitle = () => {
    switch (activePage) {
      case "voice-agents":
        return tUi("voiceAgents")
      case "user-management":
        return tUi("userManagement")
      case "call-logs":
        return tUi("callLogsRecordings")
      case "system-settings":
        return tUi("systemSettings")
      case "user-profile":
        return tUi("accountSettings")
      case "integrations":
        return tUi("integrations")
      case "email-agent":
        return tUi("eMailAgent")
      case "email-agents":
        return tUi("emailAgents")
      case "shopify-dashboard":
        return tUi("shopifyDashboard")
      case "shopify-orders":
        return tUi("shopifyOrders")
      case "shopify-products":
        return tUi("crossSellAnalysis")
      case "shopify-economics":
        return tUi("productEconomics")
      case "shopify-bundle-rules":
        return tUi("bundlesFamilies")
      case "tenant-management":
        return tUi("tenantManagement")
      case "ai-settings":
        return tUi("aiSettings")
      case "knowledge-base":
        return tUi("knowledgeBase")
      case "ai-logs":
        return tUi("aILogs")
      case "qa-feedback":
        return tUi("qaFeedback")
      case "pbx":
        return tUi("phoneNumbers")
      case "messaging-agents":
        return tUi("messagingAgents")
      case "conversation-quality":
        return tUi("conversationQuality")
      case "message-conversations":
      case "agent-emails":
        return tUi("conversations")
      case "billing":
        return tUi("billing")
      case "billing-admin":
        return tUi("billingAdmin")
      case "system-notifications":
        return tUi("systemNotifications")
      case "api-keys":
        return tUi("aPIKeys")
      case "partner-program":
        return tUi("partnerProgram")
      case "partner-admin":
        return tUi("partnerProgram")
      case "campaigns":
        return tUi("campaigns")
      case "agent-tasks":
        return tUi("agentTasks")
      case "unified-inbox":
        return tUi("chats")
      default:
        return tUi("dashboard")
    }
  }

  return (
    <div
      data-document-scroll
      className="mx-auto flex min-h-dvh max-w-[1920px] flex-col overflow-x-clip border-l border-border bg-background pb-safe-bottom pl-safe-left pr-safe-right pt-safe-top text-foreground shadow-sm transition-colors md:h-dvh md:flex-row md:overflow-x-visible"
    >
      <AppSidebar
        user={user}
        sections={sidebarSections}
        pinnedItems={pinnedItems}
        currentPage={currentPage}
        openSectionIds={openSectionIds}
        expandedItems={expandedItems}
        platformLogoUrl={platformLogoUrl}
        platformName={platformName}
        isCustomLogo={isCustomLogo}
        homePage="billing"
        buildPageHref={buildPageHref}
        onNavigate={handlePageChange}
        onToggleSection={toggleSection}
        onToggleItem={toggleSidebarItem}
        onLogout={onLogout}
        lockAllOtherPages={isTenantInactive}
        unlockedPage="billing"
        topBarCenter={canSwitchTenant ? <TenantSwitcher /> : undefined}
      />

      {/* Right Content Area */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-muted/60">
        {isTenantInactive ? <PlanExpiredDialog /> : null}
        {/* Header */}
        {/* Below `md` the top bar carries the tenant switcher and the page names itself, so
            this row is hidden there — stage's sticky treatment of it applied to a row that no
            longer renders at that width. */}
        <div className="hidden items-center justify-between gap-3 border-b border-r border-border bg-card p-4 md:flex">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
              <div className="h-4 w-4 shrink-0 rounded bg-blue-600 dark:bg-blue-500"></div>
              <ChevronRight className="h-4 w-4 shrink-0" />
              <span className="truncate">{getPageTitle()}</span>
            </div>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-3">
            <TenantInfo />
            {canSwitchTenant && <TenantSwitcher />}
          </div>
        </div>

        {/* Verify-email banner — only when the claim is explicitly false
            (undefined on legacy tokens → hidden). */}
        {user.email_verified === false && <VerifyEmailBanner email={user.email} />}

        {/* Low Balance Banner */}
        {can(BILLING_PERMISSIONS.VIEW) && <LowBalanceBanner />}

        {/* First catalog + orders load after a store is connected */}
        {can(SHOPIFY_PERMISSIONS.VIEW) && <InitialSyncBanner />}

        {/* Finishes the App-Store "add store to existing account" flow (?shopify_attach=1) */}
        <ShopifyAttachHandler />

        {/* Content. The scroller from `md` up, and at every width for a page that
            declares `data-app-scroll-pane` (see globals.css). */}
        <div
          data-shell-content
          className="min-w-0 flex-1 md:min-h-0 md:overflow-auto"
          style={{ scrollbarGutter: "stable" }}
        >
          {activePage === "voice-agents" ? (
            <VoiceAgentsPage />
          ) : activePage === "user-management" ? (
            <UserManagementWithInvite />
          ) : activePage === "system-settings" ? (
            <SystemSettingsPage />
          ) : activePage === "ai-settings" ? (
            <AISettingsPage />
          ) : activePage === "user-profile" ? (
           <UserProfilePage />
          ) : activePage === "integrations" ? (
            <IntegrationsPage />
          ) : activePage === "notification-channels" ? (
            <NotificationChannelsPage />
          ) : activePage === "system-notifications" ? (
            <SystemNotificationsPage />
          ) : activePage === "api-keys" ? (
            <ApiKeysPage />
          ) : activePage === "partner-program" ? (
            <PartnerProgramPage />
          ) : activePage === "partner-admin" ? (
            <PartnerAdminPage />
          ) : activePage === "email-agent" ? (
            // Legacy page - redirect to new email-agents
            <EmailAgentsPage />
          ) : activePage === "email-agents" ? (
            <EmailAgentsPage />
          ) : activePage === "agent-emails" ? (
            <EmailAgentInboxPage />
          ) : activePage === "conversation-quality" ? (
            <ConversationQualityPage />
          ) : activePage === "agent-analytics" ? (
            <AgentAnalyticsDashboard />
          ) : activePage === "agent-feedback" ? (
            <QAFeedbackPage />
          ) : activePage === "shopify-dashboard" ? (
            <ShopifyDashboardPage />
          ) : activePage === "shopify-orders" ? (
            <ShopifyOrdersPage />
          ) : activePage === "shopify-products" ? (
            <ShopifyInsightsGate><CrossSellPage /></ShopifyInsightsGate>
          ) : activePage === "shopify-economics" ? (
            <ShopifyInsightsGate><EconomicsPage /></ShopifyInsightsGate>
          ) : activePage === "shopify-catalog" ? (
            <CatalogPage />
          ) : activePage === "shopify-bundle-rules" ? (
            <ShopifyInsightsGate><BundleRulesPage /></ShopifyInsightsGate>
          ) : activePage === "call-logs" ? (
            <CallsPage />
          ) : activePage === "tenant-management" ? (
            <TenantManagementPage />
          ) : activePage === "knowledge-base" ? (
            <KnowledgeBasePage />
          ) : activePage === "ai-logs" ? (
            <div className="p-6 max-w-7xl mx-auto">
              <AILogsView />
            </div>
          ) : activePage === "qa-feedback" ? (
            <div className="p-6 max-w-7xl mx-auto">
              <QAFeedbackPage />
            </div>
          ) : activePage === "pbx" ? (
            <PbxPage />
          ) : activePage === "messaging-agents" ? (
            <MessagingAgentsPage />
          ) : activePage === "message-conversations" ? (
            <ConversationsInboxPage
              key={searchParams.get("agentId") || ""}
              agentId={searchParams.get("agentId") || ""}
              onBack={() => handlePageChange("messaging-agents")}
            />
          ) : activePage === "billing" ? (
            <TenantBillingPage forceUpgradePrompt={isTenantInactive} />
          ) : activePage === "billing-admin" ? (
            <BillingPage />
          ) : activePage === "campaigns" ? (
            <CampaignsPage />
          ) : activePage === "agent-tasks" ? (
            <AgentTasksPage
              canManage={can(AGENT_TASKS_PERMISSIONS.MANAGE)}
              canScheduleEmail={can(EMAIL_AGENTS_PERMISSIONS.MANAGE)}
              canScheduleMessage={can(MESSAGING_AGENTS_PERMISSIONS.MANAGE)}
            />
          ) : activePage === "unified-inbox" ? (
            <UnifiedInboxPage />
          ) : null}
        </div>
      </div>
    </div>
  )
}


/**
 * Opening Chats clears the badge: the visit is stamped once the summary for
 * the previous boundary has resolved, so the operator sees what was waiting
 * before it drops to zero. Changing the boundary re-keys the query, which is
 * what refetches — an invalidation would repeat the same count.
 */
function useStampInboxVisit(
  isOnInbox: boolean,
  countedAt: string | undefined,
  visitKey: string,
  onStamped: (at: string) => void,
) {
  const stamped = useRef(false)

  useEffect(() => {
    if (!isOnInbox) {
      stamped.current = false
      return
    }
    if (!countedAt || stamped.current) return

    stamped.current = true
    rememberInboxVisit(countedAt, inboxVisitStorage(), visitKey)
    onStamped(countedAt)
  }, [isOnInbox, countedAt, visitKey, onStamped])
}

/** Fail-closed: without data — pending or failed — the Billing nav stays hidden. */
function resolveBillingMode(
  hasBillingView: boolean,
  overview: BillingOverviewResponse | undefined,
): string {
  if (!hasBillingView) return 'none'
  if (!overview) return 'loading'
  return overview.subscription?.payment_provider ?? 'none'
}

function lockedPlanFeature(
  access: PlanFeatureAccess | undefined,
  page: PageKey | undefined,
): { feature: PlanFeature; reason: FeatureLockReason } | null {
  const feature = page ? PAGE_PLAN_FEATURES[page] : undefined
  const featureAccess = feature ? access?.[feature] : undefined
  if (!feature || !featureAccess || featureAccess.available) return null
  return { feature, reason: featureAccess.reason }
}

function withPlanLocks(
  access: PlanFeatureAccess | undefined,
  canViewBilling: boolean,
  sections: SidebarSection<PageKey>[],tUi: ReturnType<typeof useTranslations<"Shell.Page">>
): SidebarSection<PageKey>[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      const lock = lockedPlanFeature(access, item.page)
      if (!lock) return item
      const hint = featureLockHint(lock.feature, lock.reason)
      return {
        ...item,
        isDisabled: () => true,
        disabledHint: canViewBilling ? hint : `${hint} Ask someone who manages billing to upgrade.`,
        disabledAction: canViewBilling ? { label: tUi("openBilling"), page: "billing" } : undefined,
      }
    }),
  }))
}

export default function VersafyApp() {
  const tUi = useTranslations("Shell.Page")

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const forceOnboarding = pathname === SIGNUP_ONBOARDING_PATH
  const { user, isAuthenticated, isLoading, login, logout, revokeSession } = useAuth()
  const { systemAccess, isLoading: accessLoading } = useSystemAccess()
  const { logoUrl: platformLogoUrl } = usePlatformLogo()

  // Check system access and force logout if disabled
  useEffect(() => {
    if (!accessLoading && !systemAccess && isAuthenticated) {
      logout()
    }
  }, [systemAccess, accessLoading, isAuthenticated, logout])

  if (isLoading || accessLoading) {
    return <AppStartupLoader />
  }

  // Show system disabled message if access is turned off
  if (!systemAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-900/40">
            <ShieldOff className="w-8 h-8 text-red-600 dark:text-red-300" />
          </div>
          <div className="text-xl font-semibold mb-2"><span key={tUi("systemAccessDisabled")}>{tUi("systemAccessDisabled")}</span></div>
          <div className="text-muted-foreground mb-4">
            <span key={tUi("theSystemIsCurrentlyUnavailablePleaseContactYour")}>{tUi("theSystemIsCurrentlyUnavailablePleaseContactYour")}</span></div>
          <div className="mt-6 flex items-center justify-center gap-2">
            <Image src={platformLogoUrl} alt={tUi("logo")} width={96} height={32} className="h-8 w-auto opacity-50" />
            <EnvironmentBadge />
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // The attach flow finishes here after sign-in; the Shopify button would reopen the pending install instead.
    return <LoginForm onLogin={login} shopifySignIn={!searchParams.has(SHOPIFY_ATTACH_PARAM)} />
  }

  return (
    <AuthenticatedApp
      user={user!}
      onLogout={logout}
      onRevokeSession={revokeSession}
      forceOnboarding={forceOnboarding}
    />
  )
}

/**
 * Wraps `Dashboard` with the archived-tenant SPA gate. The auth-boundary
 * guard at `/api/auth/{login,refresh}` only denies *new* tokens, so an
 * already-authenticated session must short-circuit here itself when the
 * current tenant flips to archived — including immediately after the
 * Danger-zone POST /archive (which invalidates the shared tenant query, so this
 * gate refetches with every other subscriber).
 */
function AuthenticatedApp({
  user,
  onLogout,
  onRevokeSession,
  forceOnboarding,
}: {
  user: AuthUser
  onLogout: () => void
  onRevokeSession: () => void
  forceOnboarding: boolean
}) {
  const tUi = useTranslations("Shell.Page")

  const searchParams = useSearchParams()
  const { tenant, isLoading } = useCurrentTenantQuery()
  const onboarding = useSignupOnboardingQuery(
    !isLoading && Boolean(tenant) && !user.isSuperUser && !user.impersonated_tenant_id,
  )

  useEffect(() => {
    const state = onboarding.data?.state
    if (searchParams.get("page") === LEGACY_SIGNUP_ONBOARDING_PAGE) {
      const params = new URLSearchParams(searchParams.toString())
      params.delete("page")
      window.location.replace(
        params.size
          ? `${SIGNUP_ONBOARDING_PATH}?${params.toString()}`
          : SIGNUP_ONBOARDING_PATH,
      )
      return
    }
    if (
      forceOnboarding ||
      !state ||
      state.status !== "in_progress"
    ) {
      return
    }
    window.location.replace(SIGNUP_ONBOARDING_PATH)
  }, [forceOnboarding, onboarding.data?.state, searchParams])

  if (
    isLoading ||
    (tenant &&
      !user.isSuperUser &&
      !user.impersonated_tenant_id &&
      onboarding.isLoading)
  ) {
    return <AppStartupLoader />
  }

  if (tenant?.archived_at && !user.isSuperUser) {
    return (
      <ArchivedWarningScreen
        tenantName={tenant.name}
        archiveScheduledPurgeAt={tenant.archive_scheduled_purge_at}
        onLogout={onLogout}
        onRevokeSession={onRevokeSession}
      />
    )
  }

  if (forceOnboarding) {
    return <OnboardingPage />
  }

  if (
    searchParams.get("page") === LEGACY_SIGNUP_ONBOARDING_PAGE ||
    onboarding.data?.state?.status === "in_progress"
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span><span key={tUi("openingYourSetup")}>{tUi("openingYourSetup")}</span></span></div>
    )
  }

  const createdOnboardingAgents =
    onboarding.data?.state.agents.filter((agent) => agent.agent_id) ?? []

  return (
    <>
      {tenant ? (
        <SupportChatContext
          userEmail={user.email}
          tenantId={tenant.id}
          tenantName={tenant.name}
        />
      ) : null}
      <Dashboard
        user={user}
        onLogout={onLogout}
        isTenantInactive={tenant?.is_active === false && !user.isSuperUser}
      />
      <SignupOnboardingReminder state={onboarding.data?.state ?? null} />
      <SignupOnboardingCompletionHint
        agentCount={createdOnboardingAgents.length}
        hasEmailAgent={createdOnboardingAgents.some(
          (agent) => agent.type === "email",
        )}
      />
    </>
  )
}
